import { useRef, useState } from "react"
import type { GuidebookPlace, GuidebookHighlight } from "../types"

interface Props {
  places: GuidebookPlace[]
  highlights: GuidebookHighlight[]
  /** Voice-aware badge label. Falls back to "Concierge pick". */
  badgeLabel?: string
}

type CategoryFilter = "All" | "restaurant" | "cafe" | "beach" | "attraction" | "nature" | "shop" | "experience"

const CATEGORY_FILTERS: { id: CategoryFilter; label: string }[] = [
  { id: "All",        label: "All" },
  { id: "restaurant", label: "🍽 Eat" },
  { id: "cafe",       label: "☕ Coffee" },
  { id: "beach",      label: "🏖 Beach" },
  { id: "attraction", label: "🎯 Attractions" },
  { id: "nature",     label: "🌿 Nature" },
  { id: "shop",       label: "🛍 Shopping" },
]

function getEmoji(type?: string): string {
  const map: Record<string, string> = {
    restaurant: "🍽", bar: "🍷", bistro: "🍽", pub: "🍺",
    cafe: "☕", bakery: "🥐", coffee: "☕", ice_cream: "🍦",
    beach: "🏖", nature: "🌿", park: "🌳", hiking: "🥾",
    attraction: "🎯", museum: "🏛", gallery: "🖼", spa: "💆",
    golf: "⛳", shop: "🛍", market: "🛒", supermarket: "🏪",
    experience: "✨",
  }
  return map[type?.toLowerCase() || ""] || "📍"
}

interface PlaceDetailProps {
  place: GuidebookPlace
  onClose: () => void
}

function PlaceDetail({ place, onClose }: PlaceDetailProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  // Show modal on mount
  useState(() => {
    setTimeout(() => dialogRef.current?.showModal(), 0)
  })

  function handleClose() {
    dialogRef.current?.close()
    onClose()
  }

  const name = place.en?.name || ""
  const desc = place.en?.short || place.en?.why || ""
  const tip = place.en?.tip || ""

  return (
    <dialog ref={dialogRef} className="gc-place-dialog" onClose={onClose}>
      <div className="gc-place-dialog__header">
        <div>
          <span className="gc-place-dialog__emoji">{getEmoji(place.type)}</span>
          <h2 className="gc-place-dialog__name">{name}</h2>
          <div className="gc-place-dialog__type">{place.type}</div>
        </div>
        <button className="gc-place-dialog__close" onClick={handleClose} aria-label="Close" type="button">
          ×
        </button>
      </div>
      <div className="gc-place-dialog__body">
        {desc && <p className="gc-place-dialog__desc">{desc}</p>}
        {place.openingHours?.summary && (
          <div className="gc-place-dialog__meta-row">
            🕐 {place.openingHours.summary}
          </div>
        )}
        {place.coordinates && (
          <div className="gc-place-dialog__meta-row">
            📍 {place.area || "Nearby"}
          </div>
        )}
        {tip && (
          <div className="gc-place-dialog__tip">
            <div className="gc-place-dialog__tip-label">Tip</div>
            {tip}
          </div>
        )}
      </div>
    </dialog>
  )
}

export function PlacesSection({ places, highlights, badgeLabel }: Props) {
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>("All")
  const [selectedPlace, setSelectedPlace] = useState<GuidebookPlace | null>(null)

  const conciergePickIds = new Set(highlights.map(h => h.place_id))

  const filteredPlaces = places.filter(p => {
    if (activeFilter === "All") return true
    return p.type?.toLowerCase() === activeFilter
  })

  const conciergePicks = places.filter(p => conciergePickIds.has(p.id))

  return (
    <>
      {conciergePicks.length > 0 && (
        <div className="gc-mb-4">
          <div className="gc-concierge-carousel">
            {conciergePicks.map(place => {
              const highlight = highlights.find(h => h.place_id === place.id)
              return (
                <button
                  key={place.id}
                  className="gc-carousel-card"
                  onClick={() => setSelectedPlace(place)}
                  type="button"
                >
                  <div className="gc-carousel-card__badge">✨ {badgeLabel || "Concierge pick"}</div>
                  <span className="gc-carousel-card__emoji">{highlight?.emoji || getEmoji(place.type)}</span>
                  <div className="gc-carousel-card__name">{place.en?.name}</div>
                  <div className="gc-carousel-card__desc">
                    {highlight?.motivation || place.en?.short || place.en?.why || ""}
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="gc-places-filters">
        {CATEGORY_FILTERS.map(f => (
          <button
            key={f.id}
            className={`gc-filter-btn${activeFilter === f.id ? " gc-filter-btn--active" : ""}`}
            onClick={() => setActiveFilter(f.id)}
            type="button"
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="gc-places-grid">
        {filteredPlaces.map((place, i) => (
          <button
            key={place.id}
            className="gc-place-card"
            style={{ animationDelay: `${i * 40}ms` }}
            onClick={() => setSelectedPlace(place)}
            type="button"
          >
            <div className="gc-place-icon">{getEmoji(place.type)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="gc-place-name">{place.en?.name}</div>
              <div className="gc-place-meta">
                {place.type && <span style={{ textTransform: "capitalize" }}>{place.type}</span>}
                {place.area && <span> · {place.area}</span>}
              </div>
              {conciergePickIds.has(place.id) && (
                <div className="gc-place-pick-badge">✨ {badgeLabel || "Concierge pick"}</div>
              )}
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--gc-text-faint)", flexShrink: 0 }}>
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        ))}
        {filteredPlaces.length === 0 && (
          <div style={{ textAlign: "center", padding: "24px 0", color: "var(--gc-text-faint)", fontSize: 14 }}>
            No places in this category yet.
          </div>
        )}
      </div>

      {selectedPlace && (
        <PlaceDetail place={selectedPlace} onClose={() => setSelectedPlace(null)} />
      )}
    </>
  )
}
